import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compact, entries, forEach, get, isArray, map } from 'lodash';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import appointmentTypesResource from '@lib/resources/appointmentTypesResource';
import { AppointmentType } from '@lib/resources/types';

import FilterList, { FilterListProps } from '../FilterList';

import { resultKey, selectAppointmentTypes } from './selectors';

interface Props extends Omit<FilterListProps, 'onSearchChange'> {
  items: AppointmentType[];
  dispatch: Dispatch;
  isGoogleFilter?: boolean;
  initialQueryItemIds?: string;
  respectSchedulerSetting?: boolean;
  handleSuccess?: () => void;
}

const GOOGLE_BASE_PARAMS = {
  'settings.dataExport.google.availabilities.enabled': true,
};
const getAllItemsForAppointmentTypesFilter = (rawAllItems: Record<string, AppointmentType>) => {
  const allItems: Record<string, AppointmentType> = {};
  forEach(entries(rawAllItems), ([key, value]) => {
    const appointmentTypeValue = value as AppointmentType;
    allItems[key] = {
      ...appointmentTypeValue,
      name: appointmentTypeValue.alternativeName || appointmentTypeValue.name,
    };
  });
  return allItems;
};

const normalizeItems = (data: AppointmentType[]) => map(data, (it) => ({ ...it, name: it.alternativeName || it.name }));

const FilterListAppointmentTypes: React.FC<Props> = ({
  isGoogleFilter,
  allItems: rawAllItems,
  items: rawItems,
  dispatch,
  idsExcluded,
  initialQueryItemIds,
  respectSchedulerSetting = false,
  handleSuccess,
  ...other
}) => {
  const allItems = React.useMemo(
    () => getAllItemsForAppointmentTypesFilter(rawAllItems as Record<string, AppointmentType>),
    [rawAllItems],
  );
  const items = React.useMemo(() => normalizeItems(rawItems), [rawItems]);

  useEffect(() => {
    dispatch(
      appointmentTypesResource.fetchBulk({
        ids: idsExcluded,
      }),
    );
  }, [dispatch, idsExcluded]);

  const handleSearch = React.useCallback(
    (page: number | undefined, limit: number, name: string | undefined) => {
      const baseParams: any = { page, limit, name };

      if (respectSchedulerSetting) {
        baseParams['settings.scheduler.enabled'] = true;
      }

      const idsParams = initialQueryItemIds && initialQueryItemIds !== 'null' ? { _id: initialQueryItemIds } : {};
      const googleParams = isGoogleFilter ? { ...GOOGLE_BASE_PARAMS, ...idsParams } : {};
      const params = { ...baseParams, ...googleParams };
      const onSuccess = (data: { _id: string }[]) => {
        const firstItem = data.length > 0 ? data[0] : null;
        const value = get(other, 'value', []);
        const hasValue = isArray(value) ? compact(value).length > 0 : value.length > 0;
        handleSuccess?.();
        if (firstItem && !hasValue) {
          other.onItemSelected([firstItem._id], true);
        }
      };

      return dispatch(
        appointmentTypesResource.fetchPage({
          params,
          meta: {
            resultKey,
            callbackSuccess: (response) => {
              const data = get(response, 'data.response', []);
              onSuccess(data);
            },
          },
        }),
      );
    },
    [dispatch, handleSuccess, initialQueryItemIds, isGoogleFilter, other, respectSchedulerSetting],
  );

  return (
    <FilterList
      hideSearchField={get(other, 'hideSearchField', false)}
      itemsLabel="appointment types"
      allItems={allItems}
      items={items}
      idsExcluded={idsExcluded}
      onSearchChange={handleSearch}
      isGoogleFilter={isGoogleFilter}
      {...other}
    />
  );
};

const withConnect = connect(
  createStructuredSelector({
    allItems: appointmentTypesResource.selectEntitiesObj,
    items: selectAppointmentTypes,
  }),
);

export default withConnect(FilterListAppointmentTypes);
